import React, { useEffect, useState } from 'react';

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  itemName?: string;
  infoText?: string;
  isLoading?: boolean;
  confirmButtonText?: string;
  itemType?: string; // e.g., "Press Release", "Publication", "Article"
}

export const PublishModal: React.FC<PublishModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  infoText,
  isLoading = false,
  confirmButtonText = 'Yes, Proceed',
  itemType = 'item'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const defaultTitle = title || `Publish ${itemType}?`;
  const defaultSubtitle = subtitle || `Are you sure you want to publish this ${itemType.toLowerCase()}?`;
  const defaultInfoText = infoText || `It will be displayed on your website.`;

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden transition-all duration-300 ease-out ${
          isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Illustration Header */}
        <div className="bg-gray-100 p-6 flex items-center justify-center">
          <img 
            src="/post-icon.png" 
            alt="Publish" 
            className="w-full h-auto max-h-48 object-contain" 
          />
        </div>

        {/* Content Area */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{defaultTitle}</h3>
          <p className="text-sm text-gray-600 mb-1">{defaultSubtitle}</p>
          <p className="text-sm text-gray-600 mb-6">{defaultInfoText}</p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#047857] rounded-lg hover:bg-[#059669] disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Publishing...' : confirmButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishModal;

